import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("token");
      // Redirect to login page if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// File service
export const fileService = {
  // Get all files
  getFiles: (folderId) =>
    api.get(`/api/files${folderId ? `?folderId=${folderId}` : ""}`),

  // Get file by ID
  getFile: (id) => api.get(`/api/files/${id}`),

  // Create a new file
  createFile: (fileData) => api.post("/api/files", fileData),

  // Update a file
  updateFile: (id, fileData) => api.put(`/api/files/${id}`, fileData),

  // Get file versions
  getFileVersions: (id) => api.get(`/api/files/${id}/versions`),

  // Create a new file version
  createFileVersion: (id, versionData) =>
    api.post(`/api/files/${id}/versions`, versionData),

  // Upload a new file version directly
  uploadFileVersion: (id, formData) => {
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
    return api.post(`/api/files/${id}/versions/upload`, formData, config);
  },

  // Upload a file directly
  uploadFile: (formData) => {
    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };
    return api.post("/api/files/upload", formData, config);
  },

  // Create a blank Word/Excel document
  createBlank: ({ name, type, folderId }) =>
    api.post("/api/files/blank", { name, type, folderId }),

  // Delete a file
  deleteFile: (id) => api.delete(`/api/files/${id}`),

  // Share a file with a group
  shareFile: (id, groupId, accessLevel) =>
    api.post(`/api/files/${id}/share`, { groupId, accessLevel }),

  // Get file permissions
  getFilePermissions: (id) => api.get(`/api/files/${id}/permissions`),

  // Remove file permission
  removeFilePermission: (id, groupId) =>
    api.delete(`/api/files/${id}/permissions`, {
      data: { groupId },
    }),
};

// Group service
export const groupService = {
  // Get all groups
  getGroups: () => api.get("/api/groups"),

  // Get group by ID
  getGroup: (id) => api.get(`/api/groups/${id}`),

  // Create a new group
  createGroup: (groupData) => api.post("/api/groups", groupData),

  // Update a group
  updateGroup: (id, groupData) => api.put(`/api/groups/${id}`, groupData),

  // Delete a group
  deleteGroup: (id) => api.delete(`/api/groups/${id}`),

  // Add a user to a group
  addUserToGroup: (groupId, email, role) =>
    api.post(`/api/groups/${groupId}/members`, { email, role }),

  // Remove a user from a group
  removeUserFromGroup: (groupId, userId) =>
    api.delete(`/api/groups/${groupId}/members/${userId}`),
};

// Folder service
export const folderService = {
  // Get all folders
  getFolders: (parentId) =>
    api.get(`/api/folders${parentId ? `?parentId=${parentId}` : ""}`),

  // Get folder by ID
  getFolder: (id) => api.get(`/api/folders/${id}`),

  // Create a new folder
  createFolder: (folderData) => api.post("/api/folders", folderData),

  // Update a folder
  updateFolder: (id, folderData) => api.put(`/api/folders/${id}`, folderData),

  // Delete a folder
  deleteFolder: (id) => api.delete(`/api/folders/${id}`),

  // Move a folder
  moveFolder: (id, targetFolderId) =>
    api.post(`/api/folders/${id}/move`, { targetFolderId }),

  // Share a folder
  shareFolder: (id, shareData) =>
    api.post(`/api/folders/${id}/share`, shareData),

  // Get folder permissions
  getFolderPermissions: (id) => api.get(`/api/folders/${id}/permissions`),

  // Remove folder permission
  removeFolderPermission: (id, groupId) =>
    api.delete(`/api/folders/${id}/permissions`, {
      data: { groupId },
    }),

  // Global search
  globalSearch: (searchTerm) =>
    api.get(`/api/folders/search?q=${encodeURIComponent(searchTerm)}`),
};

// User service
export const userService = {
  // Get all users
  getAllUsers: () => api.get("/api/users"),

  // Get user by ID
  getUserById: (id) => api.get(`/api/users/${id}`),

  // Create user
  createUser: (userData) => api.post("/api/auth/register", userData),

  // Update user
  updateUser: (id, userData) => api.put(`/api/users/${id}`, userData),

  // Delete user
  deleteUser: (id) => api.delete(`/api/users/${id}`),
};

// OnlyOffice service
export const onlyofficeService = {
  getConfig: (fileId) => api.get(`/api/onlyoffice/config/${fileId}`),
  checkEditable: (fileId) => api.get(`/api/onlyoffice/editable/${fileId}`),
};

// Tag service
export const tagService = {
  // Get all tags
  getAllTags: () => api.get("/api/tags"),

  // Create a tag
  createTag: (tagData) => api.post("/api/tags", tagData),

  // Update a tag
  updateTag: (id, tagData) => api.put(`/api/tags/${id}`, tagData),

  // Delete a tag
  deleteTag: (id) => api.delete(`/api/tags/${id}`),

  // Add tag to folder
  addTagToFolder: (folderId, tagId) =>
    api.post(`/api/tags/folders/${folderId}/tags/${tagId}`),

  // Remove tag from folder
  removeTagFromFolder: (folderId, tagId) =>
    api.delete(`/api/tags/folders/${folderId}/tags/${tagId}`),

  // Get tags for a folder
  getFolderTags: (folderId) => api.get(`/api/tags/folders/${folderId}`),
};

// Auth service
export const authService = {
  // Login
  login: (email, password) => api.post("/api/auth/login", { email, password }),

  // Register
  register: (userData) => api.post("/api/auth/register", userData),

  // Get current user
  getCurrentUser: () => api.get("/api/auth/me"),
};
